/**
 * client/src/briefs/format.ts — display formatting for the shelf, and nothing else.
 *
 * THE ONE RULE THAT MATTERS HERE: money arrives as a string and is formatted as a string. Nothing
 * in this file adds, subtracts or compares two figures. Every delta on the timeline was computed
 * server-side over `Decimal`; the browser's job is to put a `$` and some commas on it.
 *
 * The second rule: `null` is unknown, and unknown renders as an em dash WITH A REASON — never as
 * `$0`, never as a blank cell that reads as "we checked and there was nothing".
 */

export const DASH = '—';

/** `"512500.00"` -> `"$512,500"`. Cents are dropped because a property valuation to the cent is
 * false precision, and the underlying string is untouched either way. */
export function money(value: string | null | undefined): string | null {
  if (value === null || value === undefined || value === '') return null;
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return null;
  return parsed.toLocaleString(undefined, {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  });
}

/** A signed movement: `"15000.00"` -> `"+$15,000"`, `"-8000.00"` -> `"−$8,000"` (a real minus
 * sign, U+2212, not a hyphen — at 13px a hyphen in front of a figure is easy to miss). */
export function delta(value: string | null | undefined): { text: string; direction: 'up' | 'down' | 'flat' } | null {
  if (value === null || value === undefined || value === '') return null;
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return null;
  const magnitude = Math.abs(parsed).toLocaleString(undefined, {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  });
  if (parsed === 0) return { text: `no change`, direction: 'flat' };
  return parsed > 0
    ? { text: `+${magnitude}`, direction: 'up' }
    : { text: `−${magnitude}`, direction: 'down' };
}

/** An ISO timestamp -> "12 Mar 2026". Returns null rather than "Invalid Date". */
export function shortDate(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return null;
  return at.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
}

/** The vertical, in the words a CRE professional uses for it. */
const KIND_LABELS: Record<string, string> = {
  // The product's own name for it (step 1 of the builder, the public site). Was "Appraisal-method brief" (2026-10-18).
  amb: 'Advanced Market Brief',
  cma: 'Comparative market analysis',
  protest: 'Property-tax protest',
  bpo: "Broker's price opinion",
  appraisal_support: 'Appraisal support',
};

export function kindLabel(kind: string | null | undefined): string {
  if (!kind) return 'Brief';
  return KIND_LABELS[kind] ?? kind;
}

/** Short form, for a chip beside an address. */
const KIND_SHORT: Record<string, string> = {
  amb: 'AMB',
  cma: 'CMA',
  protest: 'Protest',
  bpo: 'BPO',
  appraisal_support: 'Appraisal',
};

export function kindShort(kind: string | null | undefined): string {
  if (!kind) return 'Brief';
  return KIND_SHORT[kind] ?? kind;
}

/**
 * What a row is called. The user's own title wins; otherwise the address the report itself carries;
 * otherwise a dash and the reason. A row is never nameless and never invents a name.
 */
export function briefTitle(row: { title: string | null; subject_address: string | null }): {
  text: string;
  isPlaceholder: boolean;
} {
  const named = row.title?.trim() || row.subject_address?.trim();
  return named
    ? { text: named, isPlaceholder: false }
    : { text: `${DASH} no address on this brief yet`, isPlaceholder: true };
}

/**
 * How a status reads to a person. `done` is not a state anyone says out loud about their own work.
 * Anything unrecognised is passed through rather than swallowed — a status this list has not seen
 * before is information, not a rendering problem.
 */
const STATUS_LABELS: Record<string, string> = {
  queued: 'Queued',
  running: 'Building',
  done: 'Ready',
  failed: "Didn't finish",
  error: "Didn't finish",
  cancelled: 'Cancelled',
};

export function statusLabel(status: string | null | undefined): string | null {
  if (!status) return null;
  return STATUS_LABELS[status] ?? status;
}

export function isFinished(status: string | null | undefined): boolean {
  return status === 'done';
}

/**
 * WHY there is no figure — the sentence that goes after the em dash.
 *
 * It lives here rather than in either screen because the list and the timeline must not give two
 * different explanations for the same row. A failed brief is not "not built yet"; a finished brief
 * with no figure is not "didn't finish". Both are true statements about different states, and
 * getting them the wrong way round tells a user their completed work is still running.
 */
export function missingFigureReason(status: string | null | undefined): string {
  if (status === "failed" || status === "error") return "this brief didn't finish";
  if (status === "cancelled") return "this brief was cancelled";
  if (status === "queued" || status === "running") return "not built yet";
  if (isFinished(status)) return "no figure on this brief";
  return "no figure recorded";
}
